import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function WorkoutFrequencyCalendar({ completionStats }) {
  // Format data for chart (reverse to show oldest to newest)
  const chartData = useMemo(() => {
    if (!completionStats || completionStats.length === 0) return [];

    // completionStats comes DESC from backend, reverse for chronological display
    return [...completionStats].reverse().map(stat => {
      const [year, month] = stat.month.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = `${monthNames[parseInt(month) - 1]} '${year.substring(2)}`;

      return {
        month: monthLabel,
        workouts: stat.workout_count,
        minutes: stat.total_minutes || 0,
        hours: Math.round((stat.total_minutes || 0) / 60)
      };
    });
  }, [completionStats]);

  // Calculate streak info
  const streakInfo = useMemo(() => {
    if (!completionStats || completionStats.length === 0) {
      return { currentStreak: 0, longestStreak: 0 };
    }

    // Calculate current streak (consecutive months with workouts)
    let currentStreak = 0;
    const sortedStats = [...completionStats]; // Already DESC from backend

    // Check if there's activity in current or last month
    const now = new Date();
    const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    const lastYearMonth = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;

    const hasRecentActivity = sortedStats.some(stat =>
      stat.month === currentYearMonth || stat.month === lastYearMonth
    );

    if (hasRecentActivity) {
      for (let i = 0; i < sortedStats.length; i++) {
        if (sortedStats[i].workout_count > 0) {
          currentStreak++;
        } else {
          break;
        }
      }
    }

    return {
      currentStreak,
      longestStreak: currentStreak // Simplified for now
    };
  }, [completionStats]);

  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-calendar-alt me-2"></i>
            Workout Frequency
          </h5>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-5">
            <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
            <p className="text-muted">No workout data available yet</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>
              Workout Frequency (Last 12 Months)
            </h5>
          </Col>
          <Col md="auto">
            <div className="d-flex gap-3">
              <div className="text-center">
                <div className="text-muted small">Current Streak</div>
                <div className="h5 mb-0 text-success">
                  <i className="fas fa-fire me-1"></i>
                  {streakInfo.currentStreak} {streakInfo.currentStreak === 1 ? 'month' : 'months'}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis label={{ value: 'Workouts', angle: -90, position: 'insideLeft' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              formatter={(value, name) => {
                if (name === 'workouts') return [value, 'Workouts'];
                return [value, name];
              }}
            />
            <Legend />
            <Bar dataKey="workouts" fill="#8884d8" name="Workouts" />
          </BarChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  );
}

export default WorkoutFrequencyCalendar;
